import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, CheckCircle2, Factory, Printer } from 'lucide-react'

import type { TeaPurchase } from '@/lib/api/tea-purchase-api'
import { MarkPaidDialog } from '@/components/tea-purchases/mark-paid-dialog'
import { MarkProcessedDialog } from '@/components/tea-purchases/mark-processed-dialog'
import { TeaPurchasePrintDialog } from '@/components/tea-purchases/tea-purchase-print-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import { useTeaPurchase } from '@/hooks/use-tea-purchase-query'
import { formatQty, formatVN, formatVND } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/tea-purchases/$id',
)({
    component: TeaPurchaseDetailPage,
})

function TeaPurchaseDetailPage() {
    const { id } = Route.useParams()
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_purchases.write')

    const { data, isLoading } = useTeaPurchase(id)
    const [openPaid, setOpenPaid] = useState(false)
    const [openProcessed, setOpenProcessed] = useState(false)
    const [openPrint, setOpenPrint] = useState(false)

    if (isLoading || !data) {
        return (
            <div className="p-6 text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    const t: TeaPurchase = data
    const isFresh = t.tea_state === 'fresh'
    const isPaid = t.payment_status === 'paid'
    const isProcessed = !!t.dry_quantity

    return (
        <div className="space-y-4 px-1 py-2 md:p-6">
            <div className="flex items-center justify-between gap-2">
                <Button variant="ghost" size="sm" asChild>
                    <Link to="/tea-purchases">
                        <ArrowLeft className="size-4 mr-1" />
                        {m.tea_purchase_title()}
                    </Link>
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOpenPrint(true)}
                >
                    <Printer className="size-4 mr-1" />
                    {m.tea_purchase_action_print_qr()}
                </Button>
            </div>

            <div className="flex items-center gap-3">
                <h1 className="text-base md:text-2xl font-semibold font-mono">
                    {t.code}
                </h1>
                {t.tea_state === 'fresh' ? (
                    <Badge variant="secondary">
                        {m.tea_purchase_state_fresh()}
                    </Badge>
                ) : (
                    <Badge variant="outline">
                        {m.tea_purchase_state_dried()}
                    </Badge>
                )}
                {isPaid ? (
                    <Badge>{m.tea_purchase_payment_paid()}</Badge>
                ) : (
                    <Badge variant="destructive">
                        {m.tea_purchase_payment_unpaid()}
                    </Badge>
                )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle>{m.tea_purchase_title()}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                        <Row
                            label={m.tea_purchase_grower()}
                            value={t.grower_name || '—'}
                        />
                        <Row
                            label={m.tea_purchase_tea_type()}
                            value={t.tea_type_name || '—'}
                        />
                        <Row
                            label={m.tea_purchase_quantity()}
                            value={`${formatQty(t.quantity)} kg`}
                        />
                        <Row
                            label={m.tea_purchase_purchase_date()}
                            value={formatVN(t.purchase_date)}
                        />
                        <Row
                            label={m.tea_purchase_note()}
                            value={t.note || '—'}
                        />
                        {t.image_url && (
                            <div>
                                <p className="text-xs text-muted-foreground mb-1">
                                    {m.tea_purchase_image()}
                                </p>
                                <a
                                    href={t.image_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <img
                                        src={t.image_url}
                                        alt={t.code}
                                        className="max-h-48 rounded border"
                                    />
                                </a>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>
                            {m.tea_purchase_section_payment()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        {isPaid ? (
                            <div className="space-y-1">
                                <p className="text-muted-foreground">
                                    {m.tea_purchase_paid_at_by({
                                        time: t.paid_at
                                            ? formatVN(t.paid_at)
                                            : '',
                                        name: t.paid_by_name || '',
                                    })}
                                </p>
                                {t.paid_amount != null && (
                                    <Row
                                        label={m.tea_purchase_paid_amount()}
                                        value={formatVND(t.paid_amount)}
                                    />
                                )}
                                {t.payment_method && (
                                    <Row
                                        label={m.tea_purchase_payment_method()}
                                        value={
                                            t.payment_method === 'cash'
                                                ? m.tea_purchase_payment_method_cash()
                                                : m.tea_purchase_payment_method_bank_transfer()
                                        }
                                    />
                                )}
                                {t.payment_proof_url && (
                                    <div>
                                        <p className="text-xs text-muted-foreground mb-1">
                                            {m.tea_purchase_payment_proof()}
                                        </p>
                                        <a
                                            href={t.payment_proof_url}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            <img
                                                src={t.payment_proof_url}
                                                alt={m.tea_purchase_payment_proof()}
                                                className="max-h-40 rounded border"
                                            />
                                        </a>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-muted-foreground">
                                {m.tea_purchase_payment_unpaid()}
                            </p>
                        )}
                        {canWrite && !isPaid && (
                            <Button onClick={() => setOpenPaid(true)}>
                                <CheckCircle2 className="size-4 mr-1" />
                                {m.tea_purchase_action_mark_paid()}
                            </Button>
                        )}
                    </CardContent>
                </Card>

                {isFresh && (
                    <Card className="md:col-span-2">
                        <CardHeader>
                            <CardTitle>
                                {m.tea_purchase_section_production()}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                            {isProcessed ? (
                                <p className="text-muted-foreground">
                                    {m.tea_purchase_processed_at_by({
                                        kg: formatQty(t.dry_quantity ?? 0),
                                        time: t.processed_at
                                            ? formatVN(t.processed_at)
                                            : '',
                                        name: t.processed_by_name || '',
                                    })}
                                </p>
                            ) : (
                                <p className="text-muted-foreground">
                                    {m.tea_purchase_not_processed()}
                                </p>
                            )}
                            {canWrite && (
                                <Button onClick={() => setOpenProcessed(true)}>
                                    <Factory className="size-4 mr-1" />
                                    {isProcessed
                                        ? m.common_edit()
                                        : m.tea_purchase_action_mark_processed()}
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>

            <MarkPaidDialog
                open={openPaid}
                onOpenChange={setOpenPaid}
                teaPurchase={t}
            />
            <MarkProcessedDialog
                open={openProcessed}
                onOpenChange={setOpenProcessed}
                teaPurchase={t}
            />
            <TeaPurchasePrintDialog
                open={openPrint}
                onOpenChange={setOpenPrint}
                teaPurchase={t}
            />
        </div>
    )
}

function Row({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex">
            <span className="w-1/3 text-muted-foreground">{label}</span>
            <span className="flex-1">{value}</span>
        </div>
    )
}
